import Layout from "./components/Layout";
import useAuth from "./hooks/useAuth";
import { Button } from "./components/Button";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import FrameBuster from "./components/Security/FrameBuster";

function App() {
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);

  const publicRoutes = [
    "/",
    "/signIn",
    "/forgot-password",
    "/reset-password",
    "/change-password",
    "/update-user-password",
    "/mfa-setup",
    "/error", 
  ];

  const isPublicRoute = publicRoutes.includes(location.pathname);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isPublicRoute && auth?.expired) {
      setAuth((prev) => ({ ...prev, expired: false }));
    }
  }, [isPublicRoute, auth?.expired, setAuth]);

  useEffect(() => {
    if (!isInitialized) return;

    if (location.pathname === "/error" || isPublicRoute) return;

    if (!auth?.token || auth?.token === "null" || auth?.token === "undefined") {
      sessionStorage.setItem("intendedDestination", location.pathname);
      navigate("/error", {
        replace: true,
        state: {
          error: {
            status: 401,
            statusText: "Unauthorized Access",
            message: "Please log in to access this page",
          },
        },
      });
      return;
    }

    if (auth?.token && auth?.mfaRequired && !auth?.mfaVerified) {
      sessionStorage.setItem("intendedDestination", location.pathname);
      navigate("/mfa-setup", { replace: true });
      return;
    }
  }, [auth, location.pathname, isInitialized, isPublicRoute, navigate]);

  const handleLoginClick = () => {
    setAuth({
      token: null,
      user: null,
      roleId: null,
      expired: false,
      mfaVerified: false,
      mfaRequired: false,
    });

    const itemsToRemove = [
      "token",
      "user",
      "roleId",
      "sidebarMenu",
      "mfaRequired",
      "mfaVerified",
      "mfaEnabled",
    ];

    itemsToRemove.forEach((item) => localStorage.removeItem(item));

    sessionStorage.removeItem("intendedDestination");

    delete axios.defaults.headers["Authorization"];

    navigate("/signIn", { replace: true });
  };

  if (!isInitialized) {
    return (
      <>
        <FrameBuster />
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-gray-50">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2"
            style={{ borderColor: "#192849" }}
          ></div>
        </div>
      </>
    );
  }

  if (auth?.expired) {
    return (
      <>
        <FrameBuster />
        <div className="fixed inset-0 z-10 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-[75vh]">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-red-100">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-red-900">
                    Your session has expired.
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Please log in again to continue using the app.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 flex justify-end">
              <Button handleClick={() => handleLoginClick()} value="Login" />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isPublicRoute) {
    return (
      <>
        <FrameBuster />
        <Outlet />
      </>
    );
  }

  if (!auth?.token || auth?.token === "null" || auth?.token === "undefined") {
    navigate("/error", {
      replace: true,
      state: {
        error: {
          status: 401,
          statusText: "Authentication Required",
          message: "Please log in to access this page",
        },
      },
    });
    return null;
  }

  return (
    <>
      <FrameBuster />
      <Layout />
    </>
  );
}

export default App;
